import { marked } from 'marked';

// Custom Renderer with Tailwind styles
const renderer = new marked.Renderer();

renderer.link = ({ href, title, text }) => {
  return `<a href="${href}" title="${
    title || ''
  }" class="text-blue-600 dark:text-blue-400 hover:underline">${text}</a>`;
};

renderer.list = ({ items, ordered }) => {
  const tag = ordered ? 'ol' : 'ul';
  const styles = ordered ? 'list-decimal list-inside ml-6 mb-4' : 'list-disc list-inside ml-6 mb-4';
  return `<${tag} class="${styles}">${items.map((item) => renderer.listitem(item)).join('')}</${tag}>`;
};

renderer.listitem = (item) => {
  const text = renderer.parser.parse(item.tokens);

  // Remove <p>...</p> wrappers from the text (if present), but keep inner content
  // Only remove if there's exactly one <p> tag pair to avoid breaking multiple paragraphs
  let textWithoutPTags = text;
  if (typeof text === 'string') {
    const pTagMatches = text.match(/<p[^>]*>/gi);
    // Only remove wrapper if there's exactly one <p> tag and the string starts/ends with it
    if (pTagMatches && pTagMatches.length === 1 && /^<p[^>]*>/i.test(text) && /<\/p>$/i.test(text)) {
      textWithoutPTags = text.replace(/^<p[^>]*>([\s\S]*?)<\/p>$/i, '$1');
    }
  }

  return `<li class="mb-1">${textWithoutPTags}</li>`;
};

renderer.code = ({ text, lang }) => {
  return `<pre class="bg-gray-900 dark:bg-zinc-950 text-gray-100 dark:text-zinc-100 p-4 rounded-lg overflow-x-auto"><code class="language-${lang}">${text}</code></pre>`;
};

renderer.blockquote = (item) => {
  return `<blockquote class="border-l-4 border-gray-300 dark:border-zinc-700 pl-4 italic text-gray-600 dark:text-zinc-400 mb-4">${renderer.parser.parse(
    item.tokens,
  )}</blockquote>`;
};

renderer.image = function () {
  return '';
};

renderer.table = function (token) {
  const header = token.header;

  return `
    <table class="mb-2">
      <thead>
        ${header
          .map((h) => `<th class="border border-gray-300 dark:border-zinc-700 px-2 py-0.5">${h.text}</th>`)
          .join('')}
      </thead>
      <tbody>
        ${token.rows
          .map(
            (b) =>
              `<tr>${b
                .map((c) => `<td class="border border-gray-300 dark:border-zinc-700 px-2 py-0.5">${c.text}</td>`)
                .join('')}</tr>`,
          )
          .join('')}
      </tbody>
    </table>
  `;
};

renderer.heading = function (token) {
  let classes = 'uppercase';
  if (token.depth === 1) {
    classes += '';
  }
  if (token.depth === 2) {
    classes += ' text-zinc-500 dark:text-zinc-400';
  }
  return `<h${token.depth} class="text-md font-bold mb-2 ${classes}">${token.text}</h${token.depth}>`;
};

renderer.strong = function ({ tokens }) {
  const text = this.parser.parseInline(tokens);
  return `<strong class="font-bold">${text}</strong>`;
};

renderer.paragraph = function ({ tokens }) {
  const text = this.parser.parseInline(tokens);
  return `<p class="mb-2">${text}</p>`;
};

marked.setOptions({ renderer });

export function MarkdownToHTML({ markdown, className }: { markdown: string; className?: string }) {
  if (!markdown) {
    return null;
  }

  return (
    <div
      className={`prose prose-xs dark:prose-invert ${className}`}
      dangerouslySetInnerHTML={{
        __html: marked.parse(markdown, { renderer }),
      }}
    />
  );
}
